import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicProposal, acceptProposal, rejectProposal } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { StatusDot } from '@/components/ui/status-dot';
import { DocumentMark } from '@/components/ui/document-mark';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/ui/field';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';

const formatCurrency = (a) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(a || 0);

export default function PublicProposal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAccept, setShowAccept] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getPublicProposal(token).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [token]);

  const handleAccept = async () => {
    if (!signerName.trim()) { toast.error('Please enter your name'); return; }
    setSubmitting(true);
    try {
      await acceptProposal(token, { signer_name: signerName });
      setData(prev => ({ ...prev, proposal: { ...prev.proposal, status: 'accepted' } }));
      setShowAccept(false);
      toast.success('Proposal accepted!');
    } catch { toast.error('Failed to accept proposal'); }
    setSubmitting(false);
  };

  const handleReject = async () => {
    setSubmitting(true);
    try {
      await rejectProposal(token, { reason: rejectReason });
      setData(prev => ({ ...prev, proposal: { ...prev.proposal, status: 'rejected' } }));
      setShowReject(false);
      toast.success('Proposal rejected');
    } catch { toast.error('Failed to reject proposal'); }
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-screen bg-paper flex items-center justify-center"><p className="text-small text-ink-muted">Loading proposal…</p></div>;
  if (!data) return <div className="min-h-screen bg-paper flex items-center justify-center"><p className="text-small text-ink-muted">Proposal not found</p></div>;

  const { proposal, freelancer, client } = data;
  const content = proposal.content || {};

  return (
    <div className="min-h-screen bg-paper px-6 py-12 md:py-20" data-testid="public-proposal-page">
      <div className="mx-auto max-w-[720px]">
        {/* Header */}
        <DocumentMark freelancer={freelancer} />

        <div className="mt-10" data-testid="proposal-card">
          {/* Title */}
          <div className="border-b border-line pb-8">
            {freelancer.business_name && (
              <p className="text-micro uppercase text-ink-subtle">{freelancer.business_name}</p>
            )}
            <h1 className="mt-3 font-display text-display-l text-ink">{proposal.title}</h1>
            <p className="mt-4 text-body text-ink-muted">
              Prepared for {client.name}{client.company ? ` at ${client.company}` : ''}
            </p>
            <p className="mt-1 text-small text-ink-subtle tabular-nums">
              {new Date(proposal.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
            {proposal.status !== 'sent' && (
              <div className="mt-4">
                <StatusDot status={proposal.status} />
              </div>
            )}
          </div>

          {/* Sections */}
          <div className="max-w-[680px] space-y-10 pt-10">
            {content.summary && <Section title="Executive summary"><p className="text-body text-ink-muted">{content.summary}</p></Section>}
            {content.client_needs?.length > 0 && (
              <Section title="Understanding your needs">
                <ul className="space-y-2">
                  {content.client_needs.map((n, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-body text-ink-muted">
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-ink-subtle" />
                      {n}
                    </li>
                  ))}
                </ul>
              </Section>
            )}
            {content.solution && <Section title="Proposed solution"><p className="whitespace-pre-wrap text-body text-ink-muted">{content.solution}</p></Section>}
            {content.timeline?.length > 0 && (
              <Section title="Project timeline">
                <div>
                  {content.timeline.map((t, i) => (
                    <div key={i} className="flex gap-5 border-t border-line py-4">
                      <span className="text-micro uppercase text-ink-subtle tabular-nums">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <div>
                        <p className="text-body text-ink">
                          {t.milestone} <span className="text-ink-subtle">({t.duration})</span>
                        </p>
                        <p className="mt-1 text-small text-ink-muted">{t.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}
            {content.pricing?.length > 0 && (
              <Section title="Investment">
                <div>
                  {content.pricing.map((p, i) => (
                    <div key={i} className="flex items-start justify-between gap-6 border-t border-line py-4">
                      <div>
                        <p className="text-body text-ink">{p.item}</p>
                        {p.description && <p className="mt-0.5 text-small text-ink-muted">{p.description}</p>}
                      </div>
                      <span className="shrink-0 text-body tabular-nums text-ink">{formatCurrency(p.amount)}</span>
                    </div>
                  ))}
                  <div className="flex items-baseline justify-between border-t border-line-strong pt-4">
                    <span className="text-micro uppercase text-ink-subtle">Total investment</span>
                    <span className="font-sans text-metric tabular-nums text-ink">{formatCurrency(proposal.total_amount)}</span>
                  </div>
                </div>
              </Section>
            )}
            {content.terms && <Section title="Terms & conditions"><p className="whitespace-pre-wrap text-small text-ink-muted">{content.terms}</p></Section>}
          </div>

          {/* Actions */}
          {proposal.status === 'sent' && (
            <div className="mt-12 flex flex-col gap-3 border-t border-line pt-8 sm:flex-row">
              <Button onClick={() => setShowAccept(true)} size="lg" className="flex-1" data-testid="accept-proposal-btn">
                Accept proposal
              </Button>
              <Button variant="outline" size="lg" onClick={() => setShowReject(true)} className="flex-1" data-testid="reject-proposal-btn">
                Decline
              </Button>
            </div>
          )}

          <p className="mt-10 text-small text-ink-subtle">
            From {freelancer.name} {freelancer.email ? `(${freelancer.email})` : ''}
          </p>
        </div>

        {/* Accept Dialog */}
        <Dialog open={showAccept} onOpenChange={setShowAccept}>
          <DialogContent data-testid="accept-dialog">
            <DialogHeader>
              <DialogTitle>Accept proposal</DialogTitle>
              <DialogDescription>Sign below to accept this proposal.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <Field
                label="Your full name (e-signature)"
                htmlFor="signer-name"
                hint="By typing your name, you agree to the terms outlined in this proposal."
              >
                <Input
                  id="signer-name"
                  value={signerName}
                  onChange={e => setSignerName(e.target.value)}
                  placeholder="Type your full name"
                  data-testid="signer-name-input"
                />
              </Field>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowAccept(false)}>Cancel</Button>
                <Button onClick={handleAccept} disabled={submitting} data-testid="confirm-accept-btn">{submitting ? 'Accepting…' : 'Accept & sign'}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={showReject} onOpenChange={setShowReject}>
          <DialogContent data-testid="reject-dialog">
            <DialogHeader>
              <DialogTitle>Decline proposal</DialogTitle>
              <DialogDescription>Let the freelancer know why you're declining (optional).</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <Field label="Reason (optional)" htmlFor="reject-reason">
                <Textarea
                  id="reject-reason"
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  placeholder="Why are you declining?"
                />
              </Field>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowReject(false)}>Cancel</Button>
                <Button onClick={handleReject} disabled={submitting} variant="destructive" data-testid="confirm-reject-btn">{submitting ? 'Declining…' : 'Decline proposal'}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="mb-3 font-display text-subhead text-ink">{title}</h2>
      {children}
    </div>
  );
}
